import React, { useState, useRef, forwardRef, useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import useControllableState from '../hooks/useControllableState'
import useMergedRef from '../hooks/useMergeRef'
import Calendar from './Calendar'
import BasePicker from './BasePicker'
import { useConfig } from '../ConfigProvider'
import capitalize from '../utils/capitalize'

dayjs.extend(customParseFormat)

const DEFAULT_INPUT_FORMAT = 'DD/MM/YYYY'

const DAY_RANGE = { min: 1, max: 31 }
const MONTH_RANGE = { min: 1, max: 12 }
const YEAR_RANGE = { min: 1900, max: 2072 }

const rangeForTokenType = (type) => {
    if (type === 'day') return DAY_RANGE
    if (type === 'month') return MONTH_RANGE
    return YEAR_RANGE
}

const parseMaskGroups = (mask) => {
    const tokens = (mask || '').match(/[A-Za-z]+/g) || ['DD', 'MM', 'YYYY']
    return tokens.map((token) => {
        const first = token[0].toUpperCase()
        if (first === 'D') return { type: 'day', length: 2 }
        if (first === 'M') return { type: 'month', length: 2 }
        return { type: 'year', length: 4 }
    })
}

const parseMaskSeparator = (mask) => {
    const match = (mask || '').match(/[^A-Za-z0-9]/)
    return match ? match[0] : '/'
}

const isValidPrefix = (prefix, length, range) => {
    if (!prefix) return true
    const scale = Math.pow(10, length - prefix.length)
    const prefixNum = parseInt(prefix, 10)
    const rangeStart = prefixNum * scale
    const rangeEnd = rangeStart + scale - 1
    return rangeEnd >= range.min && rangeStart <= range.max
}

const buildFormattedDate = (rawDigits, maskGroups, maskSeparator) => {
    let result = ''
    let groupIndex = 0
    let groupDigits = ''
    for (let i = 0; i < rawDigits.length; i++) {
        const group = maskGroups[groupIndex]
        if (!group) break
        const candidate = groupDigits + rawDigits[i]
        if (!isValidPrefix(candidate, group.length, rangeForTokenType(group.type))) {
            break
        }
        if (groupDigits.length === 0 && groupIndex > 0) {
            result += maskSeparator
        }
        groupDigits = candidate
        result += rawDigits[i]
        if (groupDigits.length === group.length) {
            groupIndex += 1
            groupDigits = ''
        }
    }
    return result
}

const NumberDatePicker = forwardRef((props, ref) => {
    const {
        className,
        clearable,
        clearButton,
        closePickerOnChange,
        dateViewCount,
        dayClassName,
        dayStyle,
        defaultMonth,
        defaultOpen,
        defaultValue,
        defaultView,
        disabled,
        disableDate,
        enableHeaderLabel,
        disableOutOfMonth,
        firstDayOfWeek,
        hideOutOfMonthDates,
        hideWeekdays,
        inputFormat,
        inputPrefix,
        inputSuffix,
        labelFormat,
        locale,
        maxDate,
        minDate,
        name,
        onBlur,
        onChange,
        onFocus,
        onDropdownClose,
        onDropdownOpen,
        openPickerOnClear,
        renderDay,
        size,
        style,
        type,
        value,
        weekendDays,
        yearLabelFormat,
        popperPlacement,
        ...rest
    } = props

    const { locale: themeLocale } = useConfig()

    const finalLocale = locale || themeLocale

    const dateFormat = inputFormat || DEFAULT_INPUT_FORMAT

    const maskGroups = useMemo(() => parseMaskGroups(dateFormat), [dateFormat])
    const maskSeparator = useMemo(() => parseMaskSeparator(dateFormat), [dateFormat])
    const dateDigitLength = useMemo(
        () => maskGroups.reduce((sum, group) => sum + group.length, 0),
        [maskGroups]
    )
    const fullDateLength = dateDigitLength + maskGroups.length - 1

    const [dropdownOpened, setDropdownOpened] = useState(defaultOpen)

    const inputRef = useRef()

    const getValidDate = (val) => {
        if (val instanceof Date) return val
        if (typeof val === 'string' && val) {
            const parsed = dayjs(val, dateFormat, finalLocale)
            if (parsed.isValid()) return parsed.toDate()
        }
        return null
    }

    const [lastValidValue, setLastValidValue] = useState(defaultValue ?? null)

    const [_value, setValue] = useControllableState({
        prop: value,
        defaultProp: defaultValue,
        onChange,
    })

    const initialParsedDate = getValidDate(_value)

    const [calendarMonth, setCalendarMonth] = useState(
        initialParsedDate || defaultMonth || new Date()
    )

    const [focused, setFocused] = useState(false)

    const [inputState, setInputState] = useState(
        initialParsedDate instanceof Date
            ? capitalize(dayjs(initialParsedDate).locale(finalLocale).format(dateFormat))
            : (typeof _value === 'string' ? _value : '')
    )

    const lastValue = useRef(inputState)

    const closeDropdown = () => {
        setDropdownOpened(false)
        onDropdownClose?.()
    }

    const openDropdown = () => {
        setDropdownOpened(true)
        onDropdownOpen?.()
    }

    useEffect(() => {
        const parsedDate = getValidDate(value)
        if (parsedDate === null && !focused) {
            setInputState('')
            lastValue.current = ''
        }

        if (parsedDate instanceof Date && !focused) {
            const formatted = capitalize(dayjs(parsedDate).locale(finalLocale).format(dateFormat))
            setInputState(formatted)
            lastValue.current = formatted
        }
    }, [value, focused, themeLocale])

    useEffect(() => {
        const parsedDefault = getValidDate(defaultValue)
        if (parsedDefault instanceof Date && inputState && !focused) {
            const formatted = capitalize(dayjs(parsedDefault).locale(finalLocale).format(dateFormat))
            setInputState(formatted)
            lastValue.current = formatted
        }
    }, [themeLocale])

    const handleValueChange = (date) => {
        setValue(date)
        const formatted = capitalize(dayjs(date).locale(finalLocale).format(dateFormat))
        setInputState(formatted)
        lastValue.current = formatted
        closePickerOnChange && closeDropdown()
        window.setTimeout(() => inputRef.current?.focus(), 0)
    }

    const handleClear = () => {
        setValue(null)
        setLastValidValue(null)
        setInputState('')
        lastValue.current = ''
        openPickerOnClear && openDropdown()
        inputRef.current?.focus()
    }

    const isDateWithinRange = (dateStr) => {
        if (!dateStr) return false
        const parts = dateStr.split(/[^0-9]/)
        if (parts.length !== maskGroups.length) return false

        const values = {}
        parts.forEach((part, idx) => {
            values[maskGroups[idx].type] = parseInt(part, 10)
        })

        const { day, month, year } = values
        if (isNaN(day) || day < DAY_RANGE.min || day > DAY_RANGE.max) return false
        if (isNaN(month) || month < MONTH_RANGE.min || month > MONTH_RANGE.max) return false
        if (isNaN(year) || year < YEAR_RANGE.min || year > YEAR_RANGE.max) return false
        return true
    }

    // Strict parse: rejects dates like 31/02 that dayjs would otherwise roll over.
    const parseInputDate = (dateStr) => {
        if (!dateStr || dateStr.length !== fullDateLength) return null
        if (!isDateWithinRange(dateStr)) return null
        const parsed = dayjs(dateStr, dateFormat, true)
        return parsed.isValid() ? parsed.toDate() : null
    }

    const setDateFromInput = () => {
        const date = parseInputDate(inputState)
        if (date) {
            let finalDate = date
            if (maxDate && dayjs(finalDate).isAfter(maxDate)) {
                finalDate = maxDate
            }
            if (minDate && dayjs(finalDate).isBefore(minDate)) {
                finalDate = minDate
            }
            setValue(finalDate)
            setLastValidValue(finalDate)
            const formatted = capitalize(dayjs(finalDate).locale(finalLocale).format(dateFormat))
            setInputState(formatted)
            lastValue.current = formatted
            setCalendarMonth(finalDate)
            return
        }

        if (lastValidValue) {
            setValue(lastValidValue)
            const formatted = capitalize(dayjs(lastValidValue).locale(finalLocale).format(dateFormat))
            setInputState(formatted)
            lastValue.current = formatted
            setCalendarMonth(lastValidValue)
        } else {
            setValue(null)
            setInputState('')
            lastValue.current = ''
        }
    }

    const handleInputBlur = (event) => {
        typeof onBlur === 'function' && onBlur(event)
        setFocused(false)
        setDateFromInput()
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            closeDropdown()
            setDateFromInput()
        }
    }

    const handleInputFocus = (event) => {
        typeof onFocus === 'function' && onFocus(event)
        setFocused(true)
    }

    const formatInput = (rawVal) => {
        const clean = rawVal.replace(/\D/g, '').slice(0, dateDigitLength)
        return buildFormattedDate(clean, maskGroups, maskSeparator)
    }

    const handleChange = (event) => {
        openDropdown()
        let val = event.target.value
        
        if (val.length < lastValue.current.length) {
            const lastCharDeleted = lastValue.current.slice(val.length)
            if (lastCharDeleted.match(/[^0-9]/)) {
                const temp = val.replace(/[^0-9]+$/, '')
                val = temp.slice(0, -1)
            }
        }
        
        const formatted = formatInput(val)
        setInputState(formatted)
        lastValue.current = formatted
        
        const date = parseInputDate(formatted)
        if (date) {
            setValue(date)
            setLastValidValue(date)
            setCalendarMonth(date)
        }
    }

    return (
        <BasePicker
            inputtable={true}
            inputtableBlurClose={false}
            dropdownOpened={dropdownOpened}
            setDropdownOpened={setDropdownOpened}
            ref={useMergedRef(ref, inputRef)}
            size={size}
            style={style}
            className={className}
            onChange={handleChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            name={name}
            inputLabel={inputState}
            clearable={
                type === 'date' ? false : clearable && !!_value && !disabled
            }
            clearButton={clearButton}
            onClear={handleClear}
            disabled={disabled}
            onDropdownClose={onDropdownClose}
            onDropdownOpen={onDropdownOpen}
            type={type}
            inputPrefix={inputPrefix}
            inputSuffix={inputSuffix}
            placement={popperPlacement}
            {...rest}
        >
            <Calendar
                locale={finalLocale}
                month={calendarMonth}
                defaultMonth={
                    defaultMonth ||
                    (_value instanceof Date ? _value : new Date())
                }
                onMonthChange={setCalendarMonth}
                value={
                    _value instanceof Date
                        ? _value
                        : _value && dayjs(_value).toDate()
                }
                onChange={handleValueChange}
                labelFormat={labelFormat}
                dayClassName={dayClassName}
                dayStyle={dayStyle}
                disableOutOfMonth={disableOutOfMonth}
                minDate={minDate}
                maxDate={maxDate}
                disableDate={disableDate}
                firstDayOfWeek={firstDayOfWeek}
                preventFocus={true}
                dateViewCount={dateViewCount}
                enableHeaderLabel={enableHeaderLabel}
                defaultView={defaultView}
                hideOutOfMonthDates={hideOutOfMonthDates}
                hideWeekdays={hideWeekdays}
                renderDay={renderDay}
                weekendDays={weekendDays}
                yearLabelFormat={yearLabelFormat}
            />
        </BasePicker>
    )
})

NumberDatePicker.defaultProps = {
    closePickerOnChange: true,
    labelFormat: {
        month: 'MMM',
        year: 'YYYY',
    },
    defaultOpen: false,
    name: 'date',
    clearable: true,
    disabled: false,
    firstDayOfWeek: 'monday',
    openPickerOnClear: false,
}

export default NumberDatePicker
